import { HttpError } from "modules/common/services/communication/http";

/** вид ошибки сохранения договора, когда проверка перед завершением нашла ошибки (IHttpDataException на бэке) */
const COMPLETION_WARNINGS = "orderCompletionWarnings";

/** тип статуса «Завершен» (OrderStatus.COMPLETED на бэке) — принудительно переводим только в него */
const COMPLETED_TYPE = "Завершен";

/** Ошибка проверки перед завершением и где её исправлять (OrderCompletionWarning на бэке) */
export interface CompletionWarning {
    text: string;
    /** вкладка карточки (OrderTabs) */
    tab: string;
    /** guid раздела («Проект»), id строки затрат («Состав затрат») */
    rowId: string;
    /** секция «Состава затрат» */
    expanded: string;
    /** поле «Основных данных» */
    field: string;
}

/** Ошибки проверки перед переводом в «Завершение» / «Завершен» */
export interface CompletionCheck {
    warnings: CompletionWarning[];
    statusId: string;
    /** может перевести в обход ошибок (режим бога, перевод в «Завершен») */
    canForce: boolean;
}

/** Договор, который переводят в «Завершен» в обход ошибок, — для окна подтверждения */
export interface ForceCompletionTarget {
    orderId: string;
    warnings: CompletionWarning[];
}

/** Ошибки проверки, если сохранение упало на них, иначе null */
export const completionCheckOf = (er: unknown): CompletionCheck | null =>
    er instanceof HttpError && er.kind === COMPLETION_WARNINGS ? (er.data as CompletionCheck) : null;

/** Статус «Завершен» из справочника */
export const completedStatus = <T extends { type: string }>(statuses: T[]) =>
    statuses.find((s) => s.type === COMPLETED_TYPE);

/**
 * Ссылка на место ошибки в карточке договора. Адрес собран вручную, а не через `routes`:
 * `common/routes` импортирует карточку договора, а она — этот модуль
 */
export const warningLink = (orderId: string, warning: CompletionWarning) => {
    const params = [`tab=${warning.tab}`];
    warning.expanded && params.push(`expanded=${warning.expanded}`);
    warning.rowId && params.push(`rowId=${warning.rowId}`);
    warning.field && params.push(`field=${warning.field}`);

    return `/orders/${orderId}#${params.join("&")}`;
};

/** Подписи вкладок для группировки списка ошибок — в порядке вкладок карточки */
const WARNING_TAB_LABELS: Record<string, string> = {
    summary: "Основные данные",
    finance: "Финансирование",
    project: "Проект",
    spending: "Состав затрат",
};

/** Ошибки по вкладкам в порядке вкладок карточки */
export const groupWarnings = (warnings: CompletionWarning[]) => {
    const order = Object.keys(WARNING_TAB_LABELS);
    const groups = new Map<string, CompletionWarning[]>();
    warnings.forEach((w) => groups.set(w.tab, [...(groups.get(w.tab) || []), w]));

    return [...groups.entries()]
        .sort(([a], [b]) => order.indexOf(a) - order.indexOf(b))
        .map(([tab, items]) => ({ tab, label: WARNING_TAB_LABELS[tab] || tab, items }));
};
